/**
 * Module color wayfinding.
 *
 * Each top-level module gets its own accent color. The primary header keeps
 * a clean bright-white active pill (Linear-style, consistent everywhere) so
 * users always know which tab they clicked, while the secondary header uses
 * these accents to surface *where you are* — module-tinted pill tabs and a
 * left-side chip. High contrast, quiet elsewhere.
 *
 * Tailwind classes MUST be written out in full — dynamic string
 * interpolation like `bg-${color}-50` gets purged.
 */

export const MODULE_ACCENTS = {
  '/dashboard': {
    name: 'Overview',
    // Left-side chip on secondary bar
    chip: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500',
    },
    // Active pill on secondary bar
    tab: {
      activeText: 'text-emerald-700',
      activeDot: 'bg-emerald-500',
      activeRing: 'ring-emerald-200',
      // Solid variant for filled active tab — polished with gradient depth
      solidBg: 'bg-gradient-to-b from-emerald-500 to-emerald-600',
      solidHover: 'hover:from-emerald-500 hover:to-emerald-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(5,150,105,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      // Underline color for inactive hover
      hoverUnderline: 'bg-emerald-400',
      // Strip variant (top wayfinding strip)
      stripBg: 'bg-emerald-500',
    },
  },
  '/leads': {
    name: 'Leads',
    chip: {
      bg: 'bg-violet-50',
      text: 'text-violet-700',
      border: 'border-violet-200',
      dot: 'bg-violet-500',
    },
    tab: {
      activeText: 'text-violet-700',
      activeDot: 'bg-violet-500',
      activeRing: 'ring-violet-200',
      solidBg: 'bg-gradient-to-b from-violet-500 to-violet-600',
      solidHover: 'hover:from-violet-500 hover:to-violet-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(124,58,237,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-violet-400',
      stripBg: 'bg-violet-500',
    },
  },
  '/patients': {
    name: 'Patients',
    chip: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      border: 'border-indigo-200',
      dot: 'bg-indigo-500',
    },
    tab: {
      activeText: 'text-indigo-700',
      activeDot: 'bg-indigo-500',
      activeRing: 'ring-indigo-200',
      solidBg: 'bg-gradient-to-b from-indigo-500 to-indigo-600',
      solidHover: 'hover:from-indigo-500 hover:to-indigo-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(79,70,229,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-indigo-400',
      stripBg: 'bg-indigo-500',
    },
  },
  '/front-desk-tasks': {
    name: 'Front Desk',
    chip: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    tab: {
      activeText: 'text-amber-700',
      activeDot: 'bg-amber-500',
      activeRing: 'ring-amber-200',
      solidBg: 'bg-gradient-to-b from-amber-500 to-amber-600',
      solidHover: 'hover:from-amber-500 hover:to-amber-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(217,119,6,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-amber-400',
      stripBg: 'bg-amber-500',
    },
  },
  '/call-tracker': {
    name: 'Call Tracker',
    chip: {
      bg: 'bg-rose-50',
      text: 'text-rose-700',
      border: 'border-rose-200',
      dot: 'bg-rose-500',
    },
    tab: {
      activeText: 'text-rose-700',
      activeDot: 'bg-rose-500',
      activeRing: 'ring-rose-200',
      solidBg: 'bg-gradient-to-b from-rose-500 to-rose-600',
      solidHover: 'hover:from-rose-500 hover:to-rose-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(225,29,72,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-rose-400',
      stripBg: 'bg-rose-500',
    },
  },
  '/inbox': {
    name: 'Inbox',
    chip: {
      bg: 'bg-sky-50',
      text: 'text-sky-700',
      border: 'border-sky-200',
      dot: 'bg-sky-500',
    },
    tab: {
      activeText: 'text-sky-700',
      activeDot: 'bg-sky-500',
      activeRing: 'ring-sky-200',
      solidBg: 'bg-gradient-to-b from-sky-500 to-sky-600',
      solidHover: 'hover:from-sky-500 hover:to-sky-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(2,132,199,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-sky-400',
      stripBg: 'bg-sky-500',
    },
  },
  '/scheduling': {
    name: 'Scheduling',
    chip: {
      bg: 'bg-teal-50',
      text: 'text-teal-700',
      border: 'border-teal-200',
      dot: 'bg-teal-500',
    },
    tab: {
      activeText: 'text-teal-700',
      activeDot: 'bg-teal-500',
      activeRing: 'ring-teal-200',
      solidBg: 'bg-gradient-to-b from-teal-500 to-teal-600',
      solidHover: 'hover:from-teal-500 hover:to-teal-700',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(15,118,110,0.35),inset_0_1px_0_0_rgba(255,255,255,0.2)]',
      hoverUnderline: 'bg-teal-400',
      stripBg: 'bg-teal-500',
    },
  },
  '/admin': {
    name: 'Admin',
    chip: {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-300',
      dot: 'bg-slate-500',
    },
    tab: {
      activeText: 'text-slate-800',
      activeDot: 'bg-slate-500',
      activeRing: 'ring-slate-300',
      solidBg: 'bg-gradient-to-b from-slate-700 to-slate-800',
      solidHover: 'hover:from-slate-700 hover:to-slate-900',
      solidShadow: 'shadow-[0_2px_6px_-1px_rgba(30,41,59,0.35),inset_0_1px_0_0_rgba(255,255,255,0.15)]',
      hoverUnderline: 'bg-slate-500',
      stripBg: 'bg-slate-700',
    },
  },
};

const DEFAULT_ACCENT = MODULE_ACCENTS['/dashboard'];

/**
 * Return the module accent config for the current pathname.
 * Supports sub-paths like /leads?status=New or /scheduling/analytics.
 */
export function getModuleAccent(pathname = '') {
  if (!pathname || pathname === '/') return MODULE_ACCENTS['/dashboard'];
  // Scheduling module encompasses several sub-paths
  if (['/availability', '/overrides', '/booking-logs', '/scheduling-analytics', '/scheduling-activity-log'].some(p => pathname.startsWith(p))) {
    return MODULE_ACCENTS['/scheduling'];
  }
  for (const key of Object.keys(MODULE_ACCENTS)) {
    if (pathname === key || pathname.startsWith(key + '/') || pathname.startsWith(key + '?')) {
      return MODULE_ACCENTS[key];
    }
  }
  return DEFAULT_ACCENT;
}
